import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Autorenew as SubscriptionsIcon,
  Update as RenewIcon,
  ArrowForward as ArrowIcon
} from '@mui/icons-material';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../utils/roles';
import NumericField from './NumericField';
import ClientAutocomplete from './tasks/ClientAutocomplete';
import { filterServiceOptions, frequencySearchText } from '../utils/serviceSearch';
import { parseLocalDate } from '../utils/dueDate';
import { SUBSCRIPTION_COMPANY_NAME, findSubscriptionCompany, postSubscriptionRenewal } from '../utils/subscriptionLedger';

// Tracker for client subscriptions. Only a renewal with "Add to ledger" checked touches banking/ledger.

const TERMS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual' }
];

// Team bookkeeping only: whether the subscription was set up in Stripe yet,
// or is handled manually
const STATUSES = [
  { value: 'new', label: 'New', color: 'info' },
  { value: 'striped', label: 'Striped', color: 'success' },
  { value: 'manual', label: 'Manual', color: 'warning' }
];
const DEFAULT_STATUS = 'new';

const getStatus = (subscription) =>
  STATUSES.find((st) => st.value === subscription?.status) || STATUSES.find((st) => st.value === DEFAULT_STATUS);

const EMPTY_FORM = {
  clientId: '',
  serviceId: '',
  term: 'annual',
  charge: null,
  vendorCost: null,
  expiryDate: '',
  notes: ''
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(amount) || 0);

// Client price of a master service (same formula Proposals uses)
const getServicePrice = (service) => {
  const internal = Number(service?.internal_cost);
  const margin = Number(service?.margin_percent);
  if (!Number.isFinite(internal)) return null;
  return Math.round(internal * (1 + (Number.isFinite(margin) ? margin : 0) / 100) * 100) / 100;
};

// Vendor cost of a master service (what we pay the vendor)
const getServiceCost = (service) => {
  const internal = Number(service?.internal_cost);
  return Number.isFinite(internal) ? Math.round(internal * 100) / 100 : null;
};

// Charge = what the client pays (stored as `cost` before Charge/Cost were split); Cost = what we pay the vendor
const getCharge = (subscription) => Number(subscription?.charge ?? subscription?.cost) || 0;
const getVendorCost = (subscription) => Number(subscription?.vendorCost) || 0;

const DAY_MS = 24 * 60 * 60 * 1000;

// Whole days from today (local midnight) until the expiry date
const getDaysLeft = (expiryDate) => {
  const expiry = parseLocalDate(expiryDate);
  if (!expiry) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((expiry - today) / DAY_MS);
};

const toDateString = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Expiry date pushed forward by one term. Month-end dates clamp
// (Jan 31 + 1 month = Feb 28/29).
const getRenewedExpiry = (expiryDate, term) => {
  const current = parseLocalDate(expiryDate);
  if (!current) return null;
  const monthsToAdd = term === 'annual' ? 12 : 1;
  const target = new Date(current.getFullYear(), current.getMonth() + monthsToAdd, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(current.getDate(), lastDay));
  return toDateString(target);
};

const formatDate = (dateStr) =>
  parseLocalDate(dateStr)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) || '—';

const getTimeLeftChip = (daysLeft) => {
  if (daysLeft === null) return { label: '—', color: 'default' };
  if (daysLeft < 0) return { label: `Expired ${-daysLeft}d ago`, color: 'error' };
  if (daysLeft === 0) return { label: 'Expires today', color: 'error' };
  if (daysLeft <= 7) return { label: `${daysLeft}d left`, color: 'error' };
  if (daysLeft <= 30) return { label: `${daysLeft}d left`, color: 'warning' };
  if (daysLeft < 60) return { label: `${daysLeft}d left`, color: 'success' };
  const months = Math.floor(daysLeft / 30);
  return { label: `${months}mo ${daysLeft - months * 30}d left`, color: 'success' };
};

export default function Subscriptions() {
  const { currentUser, userProfile, isSuperAdmin } = useAuth();
  // Super Admins can do everything; Partners can only add new subscriptions; Members only see the table
  const canModify = isSuperAdmin();
  const canAdd = canModify || userProfile?.role === ROLES.PARTNER;
  const [subscriptions, setSubscriptions] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // Renewal awaiting confirmation in the modal: { subscription, suggestedExpiry, newExpiry, addToLedger }
  const [renewTarget, setRenewTarget] = useState(null);
  const [renewing, setRenewing] = useState(false);

  useEffect(() => {
    const unsubs = [
      onSnapshot(
        query(collection(db, 'subscriptions'), orderBy('expiryDate', 'asc')),
        (snap) => {
          setSubscriptions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setLoading(false);
        },
        (error) => {
          console.error('Error loading subscriptions:', error);
          setLoading(false);
        }
      ),
      onSnapshot(query(collection(db, 'clients'), orderBy('clientName', 'asc')), (snap) =>
        setClients(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      ),
      onSnapshot(query(collection(db, 'costs'), orderBy('name', 'asc')), (snap) =>
        setServices(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      ),
      onSnapshot(query(collection(db, 'entities'), orderBy('entityName', 'asc')), (snap) =>
        setEntities(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      )
    ];
    return () => unsubs.forEach((unsub) => unsub());
  }, []);

  const activeClients = useMemo(
    () => clients.filter((c) => (c.status || 'Active').toLowerCase() === 'active'),
    [clients]
  );

  // Services sorted like the Proposals picker: category, then name
  const sortedServices = useMemo(
    () =>
      [...services].sort((a, b) => {
        const ca = String(a?.category || 'Other').toLowerCase();
        const cb = String(b?.category || 'Other').toLowerCase();
        if (ca !== cb) return ca < cb ? -1 : 1;
        return String(a?.name || '').localeCompare(String(b?.name || ''));
      }),
    [services]
  );

  const getVendorName = (service) => entities.find((e) => e.id === service?.entity_id)?.entityName || '';

  const getServiceLabel = (option) => {
    if (!option) return '';
    const period = String(option.frequency || '').replaceAll('_', ' ');
    const base = [option.category, option.name, period, getVendorName(option)].filter(Boolean).join(' - ');
    const price = getServicePrice(option);
    return price !== null ? `${base} - ${formatCurrency(price)}` : base;
  };

  const selectedService = services.find((s) => s.id === formData.serviceId) || null;

  // Vendor follows the selected product / service (saved subscriptions keep their stored vendor)
  const getSubscriptionVendorName = (subscription) =>
    subscription?.vendorName
    || subscription?.serviceSnapshot?.vendorName
    || entities.find((e) => e.id === (subscription?.vendorEntityId || subscription?.serviceSnapshot?.vendorEntityId))?.entityName
    || '';
  const dialogVendorName = editing && formData.serviceId === editing.serviceId
    ? getSubscriptionVendorName(editing)
    : getVendorName(selectedService);

  const renderVendorField = () => (
    <TextField
      fullWidth
      label="Vendor"
      value={dialogVendorName}
      placeholder="Select a product / service"
      InputLabelProps={{ shrink: true }}
      InputProps={{ readOnly: true }}
      helperText="Set automatically from the product / service"
    />
  );

  const visibleSubscriptions = useMemo(() => {
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return subscriptions;
    return subscriptions.filter((s) => {
      const text = [s.clientName, s.serviceName, s.serviceCategory, s.vendorName, s.term, getStatus(s).label, s.notes]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return words.every((w) => text.includes(w));
    });
  }, [subscriptions, search]);

  const openCreate = () => {
    if (!canAdd) return;
    setEditing(null);
    setFormData(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (subscription) => {
    if (!canModify) return;
    setEditing(subscription);
    setFormData({
      clientId: subscription.clientId || '',
      serviceId: subscription.serviceId || '',
      term: subscription.term || 'monthly',
      charge: subscription.charge ?? subscription.cost ?? null,
      vendorCost: subscription.vendorCost ?? null,
      expiryDate: subscription.expiryDate || '',
      notes: subscription.notes || ''
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const handleServiceSelect = (service) => {
    setFormData((prev) => ({
      ...prev,
      serviceId: service?.id || '',
      // Default charge (service price) and cost (vendor's internal cost); both stay editable afterwards
      charge: service ? getServicePrice(service) ?? prev.charge : prev.charge,
      vendorCost: service ? getServiceCost(service) ?? prev.vendorCost : prev.vendorCost
    }));
  };

  // Frozen copy of the service at save time. Saved subscriptions read only
  // from this, so later edits to the master service never change them.
  const buildServiceSnapshot = (service) => ({
    name: service.name || '',
    category: service.category || '',
    description: service.description || '',
    frequency: service.frequency || '',
    executionLocation: service.execution_location || '',
    vendorEntityId: service.entity_id || null,
    vendorName: getVendorName(service),
    internalCost: Number.isFinite(Number(service.internal_cost)) ? Number(service.internal_cost) : null,
    marginPercent: Number.isFinite(Number(service.margin_percent)) ? Number(service.margin_percent) : null,
    price: getServicePrice(service)
  });

  const buildServiceFields = (service) => {
    const snapshot = buildServiceSnapshot(service);
    return {
      serviceId: service.id,
      serviceSnapshot: snapshot,
      // Flat copies for the table and search
      serviceName: snapshot.name,
      serviceCategory: snapshot.category,
      vendorEntityId: snapshot.vendorEntityId,
      vendorName: snapshot.vendorName
    };
  };

  const renderServicePicker = (helperText) => (
    <Autocomplete
      options={sortedServices}
      value={selectedService}
      onChange={(_, value) => handleServiceSelect(value)}
      getOptionLabel={getServiceLabel}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(opts, { inputValue }) =>
        filterServiceOptions(opts, inputValue, (option) => {
          const price = getServicePrice(option);
          return [
            option?.name,
            option?.category,
            frequencySearchText(option?.frequency),
            getVendorName(option),
            option?.description,
            option?.execution_location,
            price !== null ? `${formatCurrency(price)} ${price.toFixed(2)}` : null
          ];
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Product / Service"
          placeholder="Search services"
          helperText={helperText}
        />
      )}
    />
  );

  const handleSave = async () => {
    if (editing ? !canModify : !canAdd) return;
    if (!formData.expiryDate || !formData.serviceId || (!editing && !formData.clientId)) {
      setSnackbar({ open: true, message: 'Client, service and expiry date are required', severity: 'error' });
      return;
    }
    // Client and term are locked once saved — only these change on edit
    const editable = {
      charge: Math.max(0, Number(formData.charge) || 0),
      vendorCost: Math.max(0, Number(formData.vendorCost) || 0),
      expiryDate: formData.expiryDate,
      notes: formData.notes || '',
      updatedAt: serverTimestamp()
    };
    try {
      setSaving(true);
      if (editing) {
        const serviceChanged = formData.serviceId !== editing.serviceId && selectedService;
        await updateDoc(doc(db, 'subscriptions', editing.id), {
          ...editable,
          ...(serviceChanged ? buildServiceFields(selectedService) : {})
        });
        setSnackbar({ open: true, message: 'Subscription updated', severity: 'success' });
      } else {
        const client = clients.find((c) => c.id === formData.clientId);
        await addDoc(collection(db, 'subscriptions'), {
          ...editable,
          ...buildServiceFields(selectedService),
          clientId: formData.clientId,
          clientName: client?.clientName || '',
          term: formData.term,
          status: DEFAULT_STATUS,
          createdAt: serverTimestamp(),
          createdBy: currentUser?.uid || ''
        });
        setSnackbar({ open: true, message: 'Subscription added', severity: 'success' });
      }
      closeDialog();
    } catch (error) {
      console.error('Error saving subscription:', error);
      setSnackbar({ open: true, message: 'Error saving subscription', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Opens the renewal confirmation modal with the suggested expiry pre-filled (editable)
  const handleRenew = (subscription) => {
    if (!canModify) return;
    const suggestedExpiry = getRenewedExpiry(subscription.expiryDate, subscription.term);
    if (!suggestedExpiry) {
      setSnackbar({ open: true, message: 'Set an expiry date before renewing', severity: 'warning' });
      return;
    }
    setRenewTarget({ subscription, suggestedExpiry, newExpiry: suggestedExpiry, addToLedger: false });
  };

  const getEntityById = (id) => (id ? entities.find((e) => e.id === id) || null : null);
  const getSubscriptionVendorEntity = (subscription) =>
    getEntityById(subscription?.vendorEntityId || subscription?.serviceSnapshot?.vendorEntityId);

  // Blocks the ledger posting when something it needs is missing; null when ready
  const getLedgerBlocker = (subscription) => {
    if (!isSuperAdmin()) return 'Only Super Admins can add bank transactions';
    if (getCharge(subscription) <= 0) return 'Set a Charge on this subscription first';
    if (!findSubscriptionCompany(entities)) return `Company entity "${SUBSCRIPTION_COMPANY_NAME}" not found`;
    if (getVendorCost(subscription) > 0 && !getSubscriptionVendorEntity(subscription)) return 'The vendor entity for this subscription was not found';
    return null;
  };

  const confirmRenew = async () => {
    if (!renewTarget || !canModify) return;
    const { subscription, newExpiry, addToLedger } = renewTarget;
    if (!parseLocalDate(newExpiry)) {
      setSnackbar({ open: true, message: 'Pick a valid new expiry date', severity: 'warning' });
      return;
    }
    const chargeUsd = getCharge(subscription);
    const costUsd = getVendorCost(subscription);
    if (addToLedger) {
      const blocker = getLedgerBlocker(subscription);
      if (blocker) {
        setSnackbar({ open: true, message: blocker, severity: 'error' });
        return;
      }
    }
    let ledger = null;
    try {
      setRenewing(true);
      if (addToLedger) {
        ledger = await postSubscriptionRenewal({
          subscription,
          companyEntity: findSubscriptionCompany(entities),
          vendorEntity: getSubscriptionVendorEntity(subscription),
          chargeUsd,
          costUsd,
          date: toDateString(new Date()),
          newExpiry,
          createdBy: currentUser?.uid || ''
        });
      }
      await updateDoc(doc(db, 'subscriptions', subscription.id), {
        expiryDate: newExpiry,
        lastRenewedAt: serverTimestamp(),
        renewals: arrayUnion({
          from: subscription.expiryDate,
          to: newExpiry,
          term: subscription.term,
          charge: chargeUsd,
          vendorCost: costUsd,
          addedToLedger: Boolean(ledger),
          ...(ledger ? {
            bankLineId: ledger.bankLineId,
            chargeLedgerEntryId: ledger.chargeLedgerEntryId,
            costLedgerEntryIds: ledger.costLedgerEntryIds
          } : {}),
          byUid: currentUser?.uid || '',
          at: new Date().toISOString()
        }),
        updatedAt: serverTimestamp()
      });
      if (editing?.id === subscription.id) {
        setFormData((prev) => ({ ...prev, expiryDate: newExpiry }));
        setEditing((prev) => (prev ? { ...prev, expiryDate: newExpiry } : prev));
      }
      setRenewTarget(null);
      setSnackbar({
        open: true,
        message: `Renewed until ${formatDate(newExpiry)}${ledger ? ' and added to the ledger' : ''}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error renewing subscription:', error);
      setSnackbar({
        open: true,
        message: ledger
          ? 'Posted to the ledger, but saving the renewal failed — update the expiry manually (do not re-post)'
          : `Error renewing subscription${addToLedger ? `: ${error.message}` : ''}`,
        severity: 'error'
      });
    } finally {
      setRenewing(false);
    }
  };

  const handleStatusChange = async (subscription, status) => {
    if (!canModify) return;
    if (status === getStatus(subscription).value) return;
    try {
      await updateDoc(doc(db, 'subscriptions', subscription.id), {
        status,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating subscription status:', error);
      setSnackbar({ open: true, message: 'Error updating status', severity: 'error' });
    }
  };

  const handleDelete = async (subscription) => {
    if (!canModify) return;
    if (!window.confirm(`Delete the ${subscription.serviceName} subscription for ${subscription.clientName}?`)) return;
    try {
      await deleteDoc(doc(db, 'subscriptions', subscription.id));
      closeDialog();
      setSnackbar({ open: true, message: 'Subscription deleted', severity: 'info' });
    } catch (error) {
      console.error('Error deleting subscription:', error);
      setSnackbar({ open: true, message: 'Error deleting subscription', severity: 'error' });
    }
  };

  const clientOptions = useMemo(
    () => activeClients.map((c) => ({ id: c.id, clientName: c.clientName || '' })),
    [activeClients]
  );

  return (
    <Container maxWidth={false} sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SubscriptionsIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Subscriptions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search client, service, vendor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 280 }}
          />
          {canAdd && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Add Subscription
            </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Term</TableCell>
                <TableCell align="right">Charge</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Time Left</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : visibleSubscriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    {subscriptions.length === 0 ? 'No subscriptions yet.' : 'No subscriptions match your search.'}
                  </TableCell>
                </TableRow>
              ) : (
                visibleSubscriptions.map((s) => {
                  const chip = getTimeLeftChip(getDaysLeft(s.expiryDate));
                  return (
                    <TableRow
                      key={s.id}
                      hover
                      onClick={canModify ? () => openEdit(s) : undefined}
                      sx={{ cursor: canModify ? 'pointer' : 'default' }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{s.clientName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{getSubscriptionVendorName(s) || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{s.serviceName}</Typography>
                        {s.serviceCategory && (
                          <Typography variant="caption" color="text.secondary">{s.serviceCategory}</Typography>
                        )}
                      </TableCell>
                      <TableCell>{TERMS.find((t) => t.value === s.term)?.label || s.term}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(getCharge(s))}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {s.term === 'annual' ? ' / yr' : ' / mo'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(getVendorCost(s))}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {s.term === 'annual' ? ' / yr' : ' / mo'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(s.expiryDate)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={chip.label} color={chip.color} variant={chip.color === 'success' ? 'outlined' : 'filled'} />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {!canModify ? (
                          <Chip size="small" label={getStatus(s).label} color={getStatus(s).color} />
                        ) : (
                          <TextField
                            select
                            size="small"
                            variant="standard"
                            value={getStatus(s).value}
                            onChange={(e) => handleStatusChange(s, e.target.value)}
                            SelectProps={{
                              disableUnderline: true,
                              renderValue: (value) => {
                                const st = STATUSES.find((x) => x.value === value);
                                return <Chip size="small" label={st?.label} color={st?.color} sx={{ cursor: 'pointer' }} />;
                              }
                            }}
                          >
                            {STATUSES.map((st) => (
                              <MenuItem key={st.value} value={st.value}>{st.label}</MenuItem>
                            ))}
                          </TextField>
                        )}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                        {canModify && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<RenewIcon fontSize="small" />}
                              onClick={() => handleRenew(s)}
                            >
                              Renew
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Subscription' : 'Add Subscription'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {editing ? (
              <>
                <Grid item xs={12}>
                  <TextField fullWidth disabled label="Client" value={editing.clientName || ''} sx={{ mt: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  {(() => {
                    const snap = editing.serviceSnapshot || {};
                    const unchanged = formData.serviceId === editing.serviceId;
                    const details = [
                      snap.name || editing.serviceName,
                      snap.category || editing.serviceCategory,
                      snap.frequency ? String(snap.frequency).replaceAll('_', ' ') : null,
                      Number.isFinite(Number(snap.price)) && snap.price !== null ? `list ${formatCurrency(snap.price)}` : null
                    ].filter(Boolean).join(' • ');
                    return (
                      <>
                        {renderServicePicker(unchanged ? `Saved: ${details}` : 'Saving replaces the stored service details')}
                        {unchanged && snap.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                            {snap.description}
                          </Typography>
                        )}
                      </>
                    );
                  })()}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderVendorField()}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    disabled
                    label="Term"
                    value={TERMS.find((t) => t.value === editing.term)?.label || editing.term || ''}
                    helperText="Client and term are locked once saved"
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <ClientAutocomplete
                    clients={clientOptions}
                    value={formData.clientId || null}
                    onChange={(clientId) => setFormData((prev) => ({ ...prev, clientId: clientId || '' }))}
                    label="Client"
                    placeholder="Search clients"
                    size="medium"
                    sx={{ width: '100%', mt: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  {renderServicePicker()}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderVendorField()}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Term"
                    value={formData.term}
                    onChange={(e) => setFormData((prev) => ({ ...prev, term: e.target.value }))}
                  >
                    {TERMS.map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}
            <Grid item xs={12} md={6}>
              <NumericField
                fullWidth
                label={formData.term === 'annual' ? 'Charge (per year)' : 'Charge (per month)'}
                value={formData.charge}
                onValueChange={(n) => setFormData((prev) => ({ ...prev, charge: n }))}
                mode="decimal"
                decimals={2}
                min={0}
                prefix="$"
                helperText="What the client pays — defaults to the service price"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumericField
                fullWidth
                label={formData.term === 'annual' ? 'Cost (per year)' : 'Cost (per month)'}
                value={formData.vendorCost}
                onValueChange={(n) => setFormData((prev) => ({ ...prev, vendorCost: n }))}
                mode="decimal"
                decimals={2}
                min={0}
                prefix="$"
                helperText={`What we pay the vendor — margin ${formatCurrency((Number(formData.charge) || 0) - (Number(formData.vendorCost) || 0))}`}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Expiry Date"
                value={formData.expiryDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, expiryDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
              {formData.expiryDate && (() => {
                const chip = getTimeLeftChip(getDaysLeft(formData.expiryDate));
                return (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Time left for renewal</Typography>
                    <Chip size="small" label={chip.label} color={chip.color} sx={{ mt: 0.5 }} />
                  </Box>
                );
              })()}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {editing && (
            <>
              <Button color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(editing)}>
                Delete
              </Button>
              <Button startIcon={<RenewIcon />} onClick={() => handleRenew({ ...editing, term: formData.term, expiryDate: formData.expiryDate, charge: formData.charge, vendorCost: formData.vendorCost })}>
                Renew
              </Button>
              <Box sx={{ flex: 1 }} />
            </>
          )}
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={22} /> : editing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Renew confirmation */}
      <Dialog open={Boolean(renewTarget)} onClose={() => !renewing && setRenewTarget(null)} maxWidth="sm" fullWidth>
        {renewTarget && (() => {
          const { subscription, suggestedExpiry, newExpiry, addToLedger } = renewTarget;
          const isAnnual = subscription.term === 'annual';
          const charge = getCharge(subscription);
          const vendorCost = getVendorCost(subscription);
          const vendorEntity = getSubscriptionVendorEntity(subscription);
          const ledgerBlocker = getLedgerBlocker(subscription);
          const currentChip = getTimeLeftChip(getDaysLeft(subscription.expiryDate));
          const nextChip = getTimeLeftChip(getDaysLeft(newExpiry));
          const setRenewField = (patch) => setRenewTarget((prev) => (prev ? { ...prev, ...patch } : prev));
          return (
            <>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RenewIcon color="primary" />
                Renew Subscription
              </DialogTitle>
              <DialogContent dividers>
                <Typography variant="subtitle1" fontWeight="bold">{subscription.clientName}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {[subscription.serviceName, getSubscriptionVendorName(subscription)].filter(Boolean).join(' • ')}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                  <Chip color="primary" label={isAnnual ? 'Annual' : 'Monthly'} sx={{ fontWeight: 600, fontSize: '0.875rem' }} />
                  <Typography variant="body2">
                    Charge <strong>{formatCurrency(charge)}</strong>{isAnnual ? ' / yr' : ' / mo'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cost {formatCurrency(vendorCost)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 2,
                    borderRadius: 1.5,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Current expiry</Typography>
                    <Typography variant="body1" fontWeight={500}>{formatDate(subscription.expiryDate)}</Typography>
                    <Chip size="small" label={currentChip.label} color={currentChip.color} sx={{ mt: 0.75 }} />
                  </Box>
                  <ArrowIcon color="action" />
                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="New expiry"
                      value={newExpiry}
                      onChange={(e) => setRenewField({ newExpiry: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      disabled={renewing}
                    />
                    <Chip size="small" label={nextChip.label} color={nextChip.color} sx={{ mt: 0.75 }} />
                  </Box>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  Suggested: {formatDate(suggestedExpiry)} ({isAnnual ? '1 year' : '1 month'} after the current expiry).
                  {newExpiry !== suggestedExpiry && (
                    <Button size="small" onClick={() => setRenewField({ newExpiry: suggestedExpiry })} sx={{ ml: 0.5, py: 0, minWidth: 0 }}>
                      Use suggested
                    </Button>
                  )}
                </Typography>

                <Box sx={{ mt: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={addToLedger}
                        onChange={(e) => setRenewField({ addToLedger: e.target.checked })}
                        disabled={renewing || Boolean(ledgerBlocker)}
                      />
                    }
                    label="Add to ledger"
                  />
                  {ledgerBlocker ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{ledgerBlocker}</Typography>
                  ) : addToLedger && (
                    <Box component="ul" sx={{ m: 0, pl: 2.5, typography: 'body2' }}>
                      <li>Bank transaction <strong>+{formatCurrency(charge)}</strong>, explained as Entity › Company › {SUBSCRIPTION_COMPANY_NAME}</li>
                      {vendorCost > 0 ? (
                        <>
                          <li>{SUBSCRIPTION_COMPANY_NAME} <strong>−{formatCurrency(vendorCost)}</strong> (vendor cost)</li>
                          <li>{vendorEntity?.entityName} <strong>+{formatCurrency(vendorCost)}</strong></li>
                        </>
                      ) : (
                        <li>No Cost set — vendor posting skipped</li>
                      )}
                    </Box>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRenewTarget(null)} disabled={renewing}>Cancel</Button>
                <Button
                  variant="contained"
                  startIcon={renewing ? null : <RenewIcon />}
                  onClick={confirmRenew}
                  disabled={renewing || !newExpiry}
                >
                  {renewing ? <CircularProgress size={22} color="inherit" /> : 'Renew'}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
